import React from "react";
import { ArrowLeft, Check } from "lucide-react";

export enum SetupStep {
  MIC_CHECK = "MIC_CHECK",
  CAMERA_CHECK = "CAMERA_CHECK",
  SCREEN_SHARE = "SCREEN_SHARE",
  MOBILE_WEBCAM = "MOBILE_WEBCAM",
}

export type SetupStepDefinition = { id: SetupStep; label: string; fullLabel: string };

export const steps: SetupStepDefinition[] = [
  { id: SetupStep.MIC_CHECK, label: "Mic", fullLabel: "Mic Check" },
  { id: SetupStep.CAMERA_CHECK, label: "Camera", fullLabel: "Camera Check" },
  { id: SetupStep.SCREEN_SHARE, label: "Screen Share", fullLabel: "Screen Share" },
  { id: SetupStep.MOBILE_WEBCAM, label: "Mobile", fullLabel: "Mobile Webcam" },
];

type SetupProgressTrackerProps = {
  currentStep: SetupStep;
  isMicComplete: boolean;
  cameraChecked: boolean;
  isScreenSharing: boolean;
  mobileConnected: boolean;
  onStepClick: (step: SetupStep) => void;
  onNavigateBack?: () => void;
  stepsConfig?: SetupStepDefinition[];
};

export const SetupProgressTracker: React.FC<SetupProgressTrackerProps> = ({
  currentStep,
  isMicComplete,
  cameraChecked,
  isScreenSharing,
  mobileConnected,
  onStepClick,
  onNavigateBack,
  stepsConfig = steps,
}) => {
  const isStepClickable = (stepId: SetupStep) => {
    if (stepId === SetupStep.MIC_CHECK) return true;
    if (stepId === SetupStep.CAMERA_CHECK) return isMicComplete;
    if (stepId === SetupStep.SCREEN_SHARE) return isMicComplete && cameraChecked;
     if (stepId === SetupStep.MOBILE_WEBCAM) return isMicComplete && cameraChecked && isScreenSharing;
    return false;
  };

  const isStepCompleted = (stepId: SetupStep) => {
    if (stepId === SetupStep.MIC_CHECK) return isMicComplete;
    if (stepId === SetupStep.CAMERA_CHECK) return cameraChecked;
    if (stepId === SetupStep.SCREEN_SHARE) return isScreenSharing;
     if (stepId === SetupStep.MOBILE_WEBCAM) return mobileConnected;
    return false;
  };

  return (
    <div className="flex flex-col gap-8 w-[141px] flex-shrink-0">
      {onNavigateBack && (
        <button
          onClick={onNavigateBack}
          className="flex items-center gap-3 text-[#676A6C] hover:text-[#000] transition-colors"
          aria-label="Exit Interview"
          type="button"
        >
          <ArrowLeft className="w-[11px] h-[11px]" strokeWidth={1.4} />
          <span className="text-base leading-[24px]">Exit Interview</span>
        </button>
      )}

      <div className="flex flex-col gap-6">
        {stepsConfig.map((step, idx) => {
          const clickable = isStepClickable(step.id);
          const completed = isStepCompleted(step.id);
          return (
            <div
              key={step.label}
              className={`flex items-center gap-2 ${clickable ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
              onClick={() => {
                if (clickable) {
                  onStepClick(step.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              {completed ? (
                <div className="w-[28px] h-[28px] bg-[#10B876] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-[16px] h-[16px] text-white" strokeWidth={2.5} />
                </div>
              ) : (
                <div
                  className={`w-[28px] h-[28px] flex items-center justify-center rounded-full border flex-shrink-0 ${
                    step.id === currentStep ? "border-[#001630] text-[#001630]" : "border-[#001630] text-[#001630]"
                  }`}
                >
                  <span className="text-base font-normal">{idx + 1}</span>
                </div>
              )}

              <span
                className={`text-sm font-normal h-[20px] leading-[20px] ${
                  completed ? "text-[#10B876]" : step.id === currentStep ? "text-[#001630]" : "text-[#001630]"
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
